import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, LoadingBlock } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { VERIFICATION_LABELS } from "@/lib/format";
import { Save, Plus, Trash2, Info } from "lucide-react";

export default function ProProfilo() {
  const qc = useQueryClient();
  const { data: prof, isLoading } = useQuery({ queryKey: ["prof-profile"], queryFn: async () => (await api.get("/professional/profile")).data });
  const { data: svcData } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const { data: offersData } = useQuery({ queryKey: ["prof-services"], queryFn: async () => (await api.get("/professional/services")).data });
  const { data: areasData } = useQuery({ queryKey: ["prof-areas"], queryFn: async () => (await api.get("/professional/areas")).data });
  const { data: munData } = useQuery({ queryKey: ["municipalities"], queryFn: async () => (await api.get("/config/municipalities")).data });

  const [form, setForm] = useState({ profession: "", registration_number: "", vat_number: "", bio: "", years_experience: 0, service_radius: 15 });
  const [sel, setSel] = useState({});
  const [muni, setMuni] = useState("");

  useEffect(() => {
    if (prof?.profile) {
      const p = prof.profile;
      setForm({ profession: p.profession || "", registration_number: p.registration_number || "", vat_number: p.vat_number || "", bio: p.bio || "", years_experience: p.years_experience || 0, service_radius: p.service_radius || 15 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prof]);

  useEffect(() => {
    const map = {};
    (offersData?.offers || []).forEach((o) => { map[o.service_id] = o.prestazioni || []; });
    setSel(map);
  }, [offersData]);

  const profile = prof?.profile;
  const services = (svcData?.services || []).filter((s) => (s.prestazioni || []).length > 0);
  const areas = areasData?.areas || [];
  const municipalities = munData?.municipalities || [];

  const save = async () => {
    try { await api.put("/professional/profile", { ...form, years_experience: Number(form.years_experience), service_radius: Number(form.service_radius) }); toast.success("Profilo salvato."); qc.invalidateQueries({ queryKey: ["prof-profile"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = (sid, slug) => setSel((prev) => {
    const cur = new Set(prev[sid] || []);
    if (cur.has(slug)) cur.delete(slug); else cur.add(slug);
    return { ...prev, [sid]: [...cur] };
  });
  const savePrestazioni = async (sid) => {
    try { await api.post("/professional/services", { service_id: sid, prestazioni: sel[sid] || [] }); toast.success("Prestazioni aggiornate."); qc.invalidateQueries({ queryKey: ["prof-services"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addArea = async () => { if (!muni) return; try { await api.post("/professional/areas", { municipality: muni }); setMuni(""); qc.invalidateQueries({ queryKey: ["prof-areas"] }); } catch (e) { toast.error(apiError(e)); } };
  const delArea = async (id) => { await api.delete(`/professional/areas/${id}`); qc.invalidateQueries({ queryKey: ["prof-areas"] }); };

  if (isLoading) return <DashboardLayout title="Profilo professionale"><LoadingBlock /></DashboardLayout>;

  return (
    <DashboardLayout title="Profilo professionale">
      <div className="space-y-6 max-w-3xl">
        <Card className="space-y-3">
          <h3 className="font-bold text-[#102A2A]">Stato verifiche (demo)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["verification_status", "Profilo"], ["board_status", "Albo"], ["insurance_status", "Polizza RC"], ["vat_status", "Partita IVA"]].map(([k, l]) => (
              <div key={k} className="text-center bg-[#EAF3EF] rounded-xl p-3">
                <p className="text-xs text-[#5C6B68] mb-1">{l}</p>
                <StatusBadge status={profile?.[k]} label={VERIFICATION_LABELS[profile?.[k]] || "—"} />
              </div>
            ))}
          </div>
          <div className="flex items-start gap-2 text-xs text-[#5C6B68] bg-white border border-[#D2E3DB] rounded-xl p-3">
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-[#087A73]" /> In questo prototipo non è richiesto il caricamento di documenti reali. La verifica è gestita dall'amministratore.
          </div>
        </Card>

        <Card className="space-y-4">
          <h3 className="font-bold text-[#102A2A]">Dati professionali</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Professione sanitaria</Label><Input data-testid="prof-profession" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} className="h-12 rounded-xl mt-1" placeholder="Es. Medico" /></div>
            <div><Label>Anni di esperienza</Label><Input data-testid="prof-experience" type="number" value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} className="h-12 rounded-xl mt-1" /></div>
            <div><Label>N. iscrizione all'albo</Label><Input data-testid="prof-registration" value={form.registration_number} onChange={(e) => setForm({ ...form, registration_number: e.target.value })} className="h-12 rounded-xl mt-1" /></div>
            <div><Label>Partita IVA</Label><Input data-testid="prof-vat" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} className="h-12 rounded-xl mt-1" /></div>
            <div><Label>Distanza massima (km)</Label><Input type="number" value={form.service_radius} onChange={(e) => setForm({ ...form, service_radius: e.target.value })} className="h-12 rounded-xl mt-1" /></div>
          </div>
          <div><Label>Breve descrizione professionale</Label><Textarea data-testid="prof-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="rounded-xl mt-1" placeholder="Descrivi la tua esperienza (senza dati clinici dei pazienti)" /></div>
          <Button data-testid="prof-save" onClick={save} className="bg-[#087A73] text-white rounded-xl h-12 px-6"><Save className="mr-2 h-4 w-4" /> Salva profilo</Button>
        </Card>

        {services.map((s) => (
          <Card key={s.id} className="space-y-4">
            <h3 className="font-bold text-[#102A2A]">{s.name} — prestazioni offerte</h3>
            <div className="flex items-start gap-2 text-sm text-[#5C6B68] bg-[#EAF3EF] rounded-xl p-3">
              <Info className="h-4 w-4 mt-0.5 shrink-0 text-[#087A73]" /> Seleziona le prestazioni che offri. Riceverai solo le richieste dei pazienti per queste prestazioni. La tariffa la concordi per ogni richiesta.
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {s.prestazioni.map((p) => (
                <label key={p.slug} className="flex items-center gap-3 cursor-pointer bg-white border border-[#D2E3DB] rounded-xl px-4 py-3">
                  <Checkbox data-testid={`prest-${s.slug}-${p.slug}`} checked={(sel[s.id] || []).includes(p.slug)} onCheckedChange={() => toggle(s.id, p.slug)} />
                  <span className="text-sm font-semibold text-[#102A2A]">{p.name}</span>
                </label>
              ))}
            </div>
            <Button data-testid={`save-prest-${s.slug}`} onClick={() => savePrestazioni(s.id)} className="bg-[#087A73] text-white rounded-xl h-11 px-5"><Save className="mr-2 h-4 w-4" /> Salva prestazioni</Button>
          </Card>
        ))}

        <Card className="space-y-4">
          <h3 className="font-bold text-[#102A2A]">Comuni serviti</h3>
          <div className="flex flex-wrap gap-2">
            {areas.map((a) => (
              <span key={a.id} className="inline-flex items-center gap-2 bg-[#E0EFEA] text-[#087A73] rounded-full px-3 py-1.5 text-sm font-semibold">
                {a.municipality}<button data-testid={`del-area-${a.id}`} onClick={() => delArea(a.id)} className="text-[#C0392B]"><Trash2 className="h-3.5 w-3.5" /></button>
              </span>
            ))}
            {areas.length === 0 && <p className="text-sm text-[#5C6B68]">Nessun comune. Aggiungine uno.</p>}
          </div>
          <div className="flex gap-3 items-end pt-2 border-t border-[#EAF3EF]">
            <div className="flex-1">
              <Label>Aggiungi comune</Label>
              <Select value={muni} onValueChange={setMuni}>
                <SelectTrigger data-testid="area-select" className="h-12 rounded-xl mt-1 bg-white"><SelectValue placeholder="Seleziona comune" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">{municipalities.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="area-add" onClick={addArea} className="bg-[#087A73] text-white rounded-xl h-12"><Plus className="mr-1 h-4 w-4" /> Aggiungi</Button>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
